"use client";

import { useLingui } from "@lingui/react/macro";
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

type AutocompleteInputProps = {
  value: string;
  onChange: (value: string) => void;
  onAdd: (value: string) => void;
  suggestions: string[];
  existingItems?: string[];
  placeholder?: string;
  id?: string;
  label?: React.ReactNode;
  addButtonLabel?: React.ReactNode;
};

export function AutocompleteInput({
  value,
  onChange,
  onAdd,
  suggestions: allSuggestions,
  existingItems = [],
  placeholder,
  id: propId,
  label,
  addButtonLabel,
}: AutocompleteInputProps) {
  const [filteredSuggestions, setFilteredSuggestions] = React.useState<
    string[]
  >([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const suggestionRefs = React.useRef<(HTMLLIElement | null)[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const componentId = React.useId();
  const inputId = propId || `${componentId}-autocomplete-input`;

  const { t } = useLingui();

  React.useEffect(() => {
    suggestionRefs.current = suggestionRefs.current.slice(
      0,
      filteredSuggestions.length,
    );
  }, [filteredSuggestions]);

  React.useEffect(() => {
    if (activeIndex !== -1 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeIndex]);

  const filterAndShowSuggestions = (inputValue: string) => {
    const newFiltered = allSuggestions.filter(
      (event) =>
        event.toLowerCase().includes(inputValue.toLowerCase()) &&
        !existingItems.includes(event),
    );
    setFilteredSuggestions(newFiltered);
    setShowSuggestions(newFiltered.length > 0);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setActiveIndex(-1);
    if (inputValue) {
      filterAndShowSuggestions(inputValue);
    } else {
      // Show all non-existing suggestions if input is empty
      const initialSuggestions = allSuggestions.filter(
        (event) => !existingItems.includes(event),
      );
      setFilteredSuggestions(initialSuggestions);
      setShowSuggestions(initialSuggestions.length > 0);
    }
  };

  const showInitialSuggestionsOnFocus = () => {
    setActiveIndex(-1);
    const initialSuggestions = allSuggestions.filter(
      (event) => !existingItems.includes(event),
    );
    setFilteredSuggestions(initialSuggestions);
    setShowSuggestions(initialSuggestions.length > 0);
  };

  const handleLocalAdd = (itemToAdd?: string) => {
    const finalItem = itemToAdd || value;
    if (finalItem.trim() === "") return;
    onAdd(finalItem);
    onChange(""); // Clear input in parent after adding
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const handleSuggestionItemClick = (suggestion: string) => {
    handleLocalAdd(suggestion);
    inputRef.current?.focus(); // Keep focus on input after selection
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prevIndex) =>
          prevIndex === filteredSuggestions.length - 1 ? 0 : prevIndex + 1,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((prevIndex) =>
          prevIndex <= 0 ? filteredSuggestions.length - 1 : prevIndex - 1,
        );
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredSuggestions.length) {
          handleLocalAdd(filteredSuggestions[activeIndex]);
        } else {
          handleLocalAdd();
        }
        setActiveIndex(-1);
        inputRef.current?.focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        setShowSuggestions(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleLocalAdd();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!showSuggestions && allSuggestions.length > 0) {
        event.preventDefault();
        showInitialSuggestionsOnFocus();
        setActiveIndex(
          event.key === "ArrowDown" ? 0 : filteredSuggestions.length - 1,
        );
      }
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2 relative">
        <Input
          ref={inputRef}
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={showInitialSuggestionsOnFocus}
          onBlur={() =>
            setTimeout(() => {
              if (
                !suggestionRefs.current.some(
                  (ref) => ref === document.activeElement?.closest("li"),
                )
              ) {
                setShowSuggestions(false);
              }
            }, 150)
          }
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-grow"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => handleLocalAdd()}
        >
          {addButtonLabel ||
            t({ message: "Add", id: "autocomplete.add.button" })}
        </Button>
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 w-full bg-background border border-border shadow-lg rounded-md mt-1 top-full">
            <ScrollArea className="h-full max-h-52">
              <ul
                className="text-sm"
                role="listbox"
                aria-activedescendant={
                  activeIndex >= 0
                    ? `${inputId}-suggestion-${activeIndex}`
                    : undefined
                }
              >
                {filteredSuggestions.map((suggestion, index) => (
                  <li
                    key={suggestion}
                    id={`${inputId}-suggestion-${index}`}
                    ref={(el) => {
                      suggestionRefs.current[index] = el;
                    }}
                    role="option"
                    aria-selected={activeIndex === index}
                    className={`px-3 py-2 cursor-pointer ${
                      activeIndex === index ? "bg-accent" : "hover:bg-accent"
                    }`}
                    onClick={() => handleSuggestionItemClick(suggestion)}
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
